import { Box, Button } from "@mui/material";
import { ConfirmationDialog } from "apps/web/src/componentLibrary";
import { useSnackbar } from "apps/web/src/components/Snackbar/SnackbarContext";
import TablePage from "apps/web/src/components/universal/TablePage";
import {
  CreateAndAssignGoalConfigurationInput,
  GoalConfigurationTemplateTableItemFragment,
  useAssignGoalConfigurationTemplateToAthleteMutation,
  useCreateGoalConfigurationTemplateMutation,
  useDeleteGoalConfigurationTemplateMutation,
  useEditGoalConfigurationTemplateMutation,
  useGoalConfigurationTemplateOffsetConnectionQuery
} from "apps/web/src/types";
import { CreateGoalConfigurationTemplateDialog } from "apps/web/src/views/GoalConfigurationTemplates/Dialogs/CreateGoalConfigurationTemplateDialog";

import { useState } from "react";
import AssignGoalConfigFromTemplateDialog from "./Dialogs/AssignGoalConfigFromTemplateDialog";
import ViewAssignedAthletesDialog from "./Dialogs/ViewAssignedAthletesDialog";
import { useGoalConfigurationTemplatePageContext } from "./GoalConfigurationTemplateProvider";
import { NoResults } from "./NoResults";
import { TableBodyRows } from "./Table/TableBodyRows";
import { TableHeaderRow } from "./Table/TableHeaderRow";
import { TablePageHeader } from "./Table/TablePageHeader";
import { CreateGoalConfigurationTemplateDialogMode, GoalConfigurationTemplateFormType, buildCreateGoalInput, buildEditGoalInput } from "./utils";

const BUTTON_GAP = 2;

export const GoalConfigurationTemplateContent = () => {
    const { setMessage } = useSnackbar();
    const { category, target, createdByMe, paginationHooks } = useGoalConfigurationTemplatePageContext();
    const { limit, offset, queryText } = paginationHooks;

    const [deleteGoal, setDeleteGoal] = useState<GoalConfigurationTemplateTableItemFragment | null>(null);
    const [selectedGoal, setSelectedGoal] = useState<GoalConfigurationTemplateTableItemFragment | null>(null);
    const [dialogMode, setDialogMode] = useState<CreateGoalConfigurationTemplateDialogMode | null>(null);
    const [loading, setLoading] = useState(false);
    const [openViewAssignedAthletes, setOpenViewAssignedAthletes] = useState(false);
    const [openAssignGoalConfigFromTemplate, setOpenAssignGoalConfigFromTemplate] = useState(false);

    const [createGoalConfigurationTemplate] = useCreateGoalConfigurationTemplateMutation();
    const [deleteGoalConfigurationTemplate] = useDeleteGoalConfigurationTemplateMutation();
    const [editGoalConfigurationTemplate] = useEditGoalConfigurationTemplateMutation();
    const [assignGoalConfigurationTemplateToAthlete] = useAssignGoalConfigurationTemplateToAthleteMutation();

    const filterOptions = {
        searchText: queryText || null,
        target: target.id === "progress" ? true : target.id === "complete" ? false : null,
        uploadedByMe: createdByMe || null,
        categoryId: category.id !== "none" ? category.id : null,
    };

    const {
        data,
        loading: loadingFetchGoalConfigTemplates,
        refetch
    } = useGoalConfigurationTemplateOffsetConnectionQuery({
        variables: { pagination: { offset, limit }, filterOptions },
        fetchPolicy: 'cache-and-network'
    });

    const tableData = data?.goalConfigurationTemplatesOffsetConnection.edges || [];

    const noGoalFound = !tableData.length &&
        !filterOptions.searchText &&
        !filterOptions.categoryId &&
        filterOptions.target === null &&
        !filterOptions.uploadedByMe &&
        !loadingFetchGoalConfigTemplates;

    const handleDeleteGoal = async () => {
        if (!deleteGoal) return;

        await deleteGoalConfigurationTemplate({
            variables: {
                input: {
                    id: deleteGoal.id
                }
            }
        });

        refetch();
        setMessage("success", `${deleteGoal.name} has been deleted`);
        setDeleteGoal(null);
    };

    const handleEditGoal = async (goal: GoalConfigurationTemplateFormType) => {
        if (!selectedGoal) return;

        await editGoalConfigurationTemplate({
            variables: {
                input: buildEditGoalInput(goal, selectedGoal)
            }
        });

        refetch();
        closeCreateGoalDialog();
        setMessage("success", `${goal.task} has been edited`);
    };

    const handleCreateGoal = async (goal: GoalConfigurationTemplateFormType) => {
        await createGoalConfigurationTemplate({
            variables: {
                input: buildCreateGoalInput(goal)
            }
        });

        refetch();
        closeCreateGoalDialog();
        setMessage("success", `${goal.task} has been created as a new goal`);
    };

    const handleAssignAthletes = async (input: CreateAndAssignGoalConfigurationInput) => {
        setLoading(true);

        const response = await assignGoalConfigurationTemplateToAthlete({
            variables: { input }
        });

        setLoading(false);
        refetch();

        const athleteCount = response.data?.assignGoalConfigurationTemplateToAthlete.length || 0;
        setMessage("success", `${selectedGoal?.name} has been assigned to ${athleteCount} athletes`);
        setSelectedGoal(null);
    };

    const closeCreateGoalDialog = () => {
        setDialogMode(null);
        setSelectedGoal(null);
    };

    const openCreateGoalDialog = () => {
        setDialogMode(CreateGoalConfigurationTemplateDialogMode.Create);
        setSelectedGoal(null);
    };

    const handleDuplicateGoal = (goal: GoalConfigurationTemplateTableItemFragment) => {
        setDialogMode(CreateGoalConfigurationTemplateDialogMode.Duplicate);
        setSelectedGoal(goal);
    };

    const onEditGoal = (goal: GoalConfigurationTemplateTableItemFragment) => {
        setDialogMode(CreateGoalConfigurationTemplateDialogMode.Edit);
        setSelectedGoal(goal);
    };

    const handleEditGoalClick = (goal: GoalConfigurationTemplateTableItemFragment) => {
        setDialogMode(goal.assignedAthleteCount > 0 ? CreateGoalConfigurationTemplateDialogMode.View : CreateGoalConfigurationTemplateDialogMode.Edit);
        setSelectedGoal(goal);
    };

    const handleViewAssignedAthletes = (goal: GoalConfigurationTemplateTableItemFragment) => {
        setSelectedGoal(goal);
        setOpenViewAssignedAthletes(true);
    };

    const handleAssignGoal = (goal: GoalConfigurationTemplateTableItemFragment) => {
        setSelectedGoal(goal);
        setOpenAssignGoalConfigFromTemplate(true);
    };

    const renderContent = () => {
        if (noGoalFound) {
            return (
                <>
                    <Box sx={{ display: "flex", gap: BUTTON_GAP, justifyContent: "flex-end" }}>
                        <Button onClick={openCreateGoalDialog}>Create Goal</Button>
                    </Box>
                    <NoResults />
                </>
            );
        }

        return (
            <TablePage
                header={<TablePageHeader onCreate={openCreateGoalDialog} />}
                tableHeaderRow={<TableHeaderRow />}
                paginationHooks={paginationHooks}
                tableBodyRows={tableData.map((data) => (
                    <TableBodyRows
                        key={data.id}
                        data={data}
                        onDelete={() => setDeleteGoal(data)}
                        onDuplicate={() => handleDuplicateGoal(data)}
                        onClick={() => handleEditGoalClick(data)}
                        onViewAssignedAthlete={() => handleViewAssignedAthletes(data)}
                        onAssignGoal={() => handleAssignGoal(data)}
                    />
                ))}
                loading={loadingFetchGoalConfigTemplates || loading}
                total={data?.goalConfigurationTemplatesOffsetConnection.pageInfo.total || 0}
            />
        );
    };

    return (
        <>
            {renderContent()}

            {dialogMode !== null && (
                <CreateGoalConfigurationTemplateDialog
                    mode={dialogMode}
                    open={dialogMode !== null}
                    onClose={closeCreateGoalDialog}
                    onSubmit={(goal) => dialogMode === CreateGoalConfigurationTemplateDialogMode.Edit ? handleEditGoal(goal) : handleCreateGoal(goal)}
                    selectedGoalConfigurationTemplate={selectedGoal}
                />
            )}

            <ConfirmationDialog
                open={deleteGoal !== null}
                title="Delete Goal"
                message="Are you sure you want to delete the goal? Doing so will remove it from this list, but not from the individual athlete(s) this has been assigned to."
                onCancel={() => setDeleteGoal(null)}
                onConfirm={handleDeleteGoal}
                variant="containedDestructive"
                confirmLabel="Yes"
            />

            {selectedGoal && openViewAssignedAthletes && (
                <ViewAssignedAthletesDialog
                    open={openViewAssignedAthletes}
                    onClose={() => setOpenViewAssignedAthletes(false)}
                    goalConfigTemplate={selectedGoal}
                />
            )}

            {selectedGoal && openAssignGoalConfigFromTemplate && (
                <AssignGoalConfigFromTemplateDialog
                    goalConfigTemplate={selectedGoal}
                    open={openAssignGoalConfigFromTemplate}
                    onClose={() => setOpenAssignGoalConfigFromTemplate(false)}
                    onSubmit={handleAssignAthletes}
                />
            )}
        </>
    );
};
